import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../environments/environment';

// Data interfaces for AI Summary service
export interface DataSummary {
  completed: number;
  total: number;
  completionRate: number; // percentage
  medianDuration: number; // seconds
}

export interface ExecutiveSummary {
  text: string;
  lastUpdated: Date;
}

export interface SentimentBreakdown {
  label: 'Neutral' | 'Positive' | 'Negative' | 'Mixed';
  percentage: number;
}

export interface SubTheme {
  name: string;
  instances: number;
  percentage: number;
}

export interface Quote {
  id: string;
  text: string;
  respondentId?: string;
  subThemes?: string[];
  sentiment?: Sentiment;
}

export interface Theme {
  id: number;
  name: string;
  subThemes: SubTheme[];
  instances: number;
  percentage: number;
  quotes?: Quote[];
}

export interface ChatbotSummary {
  questionId: string;
  dataSummary: DataSummary;
  executiveSummary: ExecutiveSummary;
  sentimentAnalysis: SentimentBreakdown[];
  themes: Theme[];
}

export interface ChatbotQuestion {
  id: string;
  text: string;
}

export interface RefreshAnalysisRequest {
  questionId: string;
  projectId?: string;
  filter?: AnalysisFilter;
  label?: string;
}

/**
 * Tag/demographic filter applied at analysis time.
 * Semantics (enforced by ai-summary): AND across keys, OR within each key's values.
 */
export interface AnalysisFilter {
  tagValues?: Record<string, string[]>;
}

/** Available values and per-value respondent counts for a single tag dimension. */
export interface ProjectTagInfo {
  values: string[];
  counts: Record<string, number>;
  /**
   * Optional display overrides. `values`/keys stay canonical (so a selection can be
   * submitted straight back as a filter); these carry human-readable text for the UI
   * only. The system language tag sets `keyLabel` to "Language" and maps each code in
   * `valueLabels` to its name (e.g. "en-US" -> "American English").
   */
  keyLabel?: string;
  valueLabels?: Record<string, string>;
}

/** Map of tag key -> its available values/counts (e.g. { region: {...}, brand: {...} }). */
export type ProjectTags = Record<string, ProjectTagInfo>;

// NOTE: the Navigator .NET backend serializes responses with System.Text.Json
// (camelCase Web defaults), so these fields are camelCase even though ai-summary
// itself returns snake_case — the proxy re-serializes on the way out.
export interface AnalysisVersion {
  versionId: number;
  versionNumber: number;
  createdAt: Date;
  description?: string | null;
  label?: string | null;
  filterSpec?: AnalysisFilter | null;
  totalRespondents?: number | null;
  themesCount?: number | null;
}

export interface AnalysisVersionsResponse {
  versions: AnalysisVersion[];
}

export interface UpdateBalanceResponse {
  message: string;
  projectId: string;
  newLimit: number;
}
export interface RemainingBalanceResponse {
  limit: number;
  total_used: number;
  remaining: number;
  executions: Array<ExecutionRecord>;
}

export interface ExecutionRecord {
  question_id: string;
  execution_count: number;
  status: 'completed' | 'in_progress' | 'failed';
  last_execution_at: Date;
}

export interface AnalysisStatusResponse {
  status: 'idle' | 'in_progress' | 'completed' | 'failed';
  projectId: string;
  questionId: string;
  createdAt: Date;
  message?: string;
}



export enum Sentiment {
  Neutral = 'Neutral',
  Positive = 'Positive',
  Negative = 'Negative',
  Mixed = 'Mixed'
}

@Injectable({
  providedIn: 'root'
})
export class AiSummaryService {
  baseUrl = environment.apiEndpoint + '/aisummary';

  constructor(private http: HttpClient) { }

  /**
   * Get chatbot summary data for a specific question
   */
  getChatbotSummary(questionId: string, projectId: string, version?: number): Observable<ChatbotSummary> {
    let url = `${this.baseUrl}/summary/${questionId}?projectId=${projectId}`;
    if (version != null) {
      url += `&version=${version}`;
    }
    return this.http.get<ChatbotSummary>(url);
  }

  /**
   * Generate the one-page branded PDF of a question's report on the backend and
   * return a presigned S3 URL to download it. Mirrors getChatbotSummary's version
   * semantics so the PDF matches the on-screen report. The backend responds with the
   * URL as a JSON string.
   */
  exportReportPdf(questionId: string, projectId: string, version?: number): Observable<string> {
    let url = `${this.baseUrl}/summary/${questionId}/pdf?projectId=${projectId}`;
    if (version != null) {
      url += `&version=${version}`;
    }
    // The endpoint returns the presigned URL as a bare string (text/plain via ASP.NET's
    // StringOutputFormatter), so read it as text — `responseType: 'json'` would try to
    // JSON.parse the raw URL and fail even on a 200.
    return this.http.get(url, { responseType: 'text' });
  }

  /**
   * Get the available tag/demographic filter dimensions for a project,
   * with distinct values and per-value respondent counts.
   *
   * Passing questionId scopes the counts to that question's included respondents so
   * they reconcile with the report's data-summary total; omitting it aggregates
   * across the whole project (all questions), whose counts can exceed any one total.
   */
  getProjectTags(projectId: string, questionId?: string): Observable<ProjectTags> {
    let url = `${this.baseUrl}/project/${projectId}/tags`;
    if (questionId) {
      url += `?questionId=${encodeURIComponent(questionId)}`;
    }
    return this.http.get<ProjectTags>(url);
  }

  /**
   * List the analysis versions (history) for a question. Each version carries the
   * filter that produced it, creation time, label and respondent/theme counts.
   */
  getVersions(questionId: string, projectId: string): Observable<AnalysisVersionsResponse> {
    return this.http.get<AnalysisVersionsResponse>(`${this.baseUrl}/questions/${questionId}/versions?projectId=${projectId}`);
  }

  /**
   * Get all available questions for a project
   */
  getChatbotQuestions(projectId: string): Observable<ChatbotQuestion[]> {
    return this.http.get<ChatbotQuestion[]>(`${this.baseUrl}/questions?projectId=${projectId}`);
  }

  /**
   * Get remaining analysis balance for the user/account
   */
  getRemainingBalance(projectId: string): Observable<RemainingBalanceResponse> {
    return this.http.get<RemainingBalanceResponse>(`${this.baseUrl}/balance/${projectId}`);
  }

  /**
   * Update the remaining analysis balance
   */
  updateRemainingBalance(projectId: string, balance: number): Observable<UpdateBalanceResponse> {
    return this.http.put<UpdateBalanceResponse>(`${this.baseUrl}/balance/${projectId}`, balance);
  }



  /**
   * Start background analysis for a specific question
   * Returns 202 response indicating background processing has started
   */
  refreshAnalysisBackground(request: RefreshAnalysisRequest): Observable<AnalysisStatusResponse> {
    return this.http.post<AnalysisStatusResponse>(`${this.baseUrl}/refresh`, request);
  }

  /**
   * Get the status of an analysis job. Pass questionId to scope the status to a single
   * question — executions are tracked per (project, question), so without it a project
   * with several questions can return another question's (often stale) status.
   */
  getAnalysisStatus(projectId: string, questionId?: string): Observable<AnalysisStatusResponse> {
    let url = `${this.baseUrl}/analysis/status/${projectId}`;
    if (questionId) {
      url += `?questionId=${encodeURIComponent(questionId)}`;
    }
    return this.http.get<AnalysisStatusResponse>(url);
  }

  /**
   * Refresh analysis and poll for completion if it runs in background
   * @param request The refresh analysis request
   * @param pollIntervalMs Polling interval in milliseconds (default: 5000)
   * @param maxPollingTimeMs Maximum polling time in milliseconds (default: 300000 = 5 minutes)
   * @returns Observable that emits the final AnalysisStatusResponse when completed
   * 
   * @example
   * // Simple usage - handles background processing and polling
   * this.aiSummaryService.refreshAnalysisWithPolling({ questionId: '123', projectId: '456' })
   *   .subscribe({
   *     next: (status) => console.log('Analysis completed:', status),
   *     error: (error) => console.error('Analysis failed:', error)
   *   });
   * 
   * @example
   * // Custom polling intervals
   * this.aiSummaryService.refreshAnalysisWithPolling(
   *   { questionId: '123', projectId: '456' },
   *   3000,  // poll every 3 seconds
   *   180000 // timeout after 3 minutes
   * ).subscribe(status => this.handleAnalysisCompletion(status));
   */
  refreshAnalysisWithPolling(
    request: RefreshAnalysisRequest, 
    pollIntervalMs: number = 10000,
    maxPollingTimeMs: number = 600000
  ): Observable<AnalysisStatusResponse> {
    return this.refreshAnalysisBackground(request).pipe(
      switchMap(response => {
        // Background processing - poll the status of THIS question. Use the request's
        // questionId (the source of truth) rather than the response, which may omit it.
        const projectId = response.projectId ?? request.projectId;
        return this.pollAnalysisStatus(projectId, request.questionId, pollIntervalMs, maxPollingTimeMs);
      })
    );
  }

  /**
   * Poll analysis status until completion
   * @param projectId The project ID to poll
   * @param questionId The question ID to scope the status to
   * @param pollIntervalMs Polling interval in milliseconds
   * @param maxPollingTimeMs Maximum polling time in milliseconds
   * @returns Observable that emits the final AnalysisStatusResponse
   */
  pollAnalysisStatus(
    projectId: string,
    questionId: string,
    pollIntervalMs: number,
    maxPollingTimeMs: number
  ): Observable<AnalysisStatusResponse> {
    const startTime = Date.now();
    // The analysis runs on the backend; a single failed status poll caused by a
    // TRANSIENT infrastructure blip must not abort the whole job, which keeps
    // running and usually completes. Tolerate a few CONSECUTIVE transient failures
    // and only surface an error if they persist. Deterministic errors (4xx, app
    // 500) won't fix themselves on retry, so those are surfaced immediately.
    const maxConsecutiveErrors = 3;
    // Gateway family (502/503/504) plus status 0 (network-level failure: dropped
    // connection, CORS, aborted) are the errors that typically clear on retry.
    const transientStatuses = new Set([0, 502, 503, 504]);

    return new Observable<AnalysisStatusResponse>(subscriber => {
      let cancelled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let consecutiveErrors = 0;

      const scheduleNext = () => {
        if (cancelled) return;
        if (Date.now() - startTime >= maxPollingTimeMs) {
          subscriber.error(new Error('Analysis polling timeout - the analysis did not complete within the expected timeframe'));
          return;
        }
        timer = setTimeout(pollFn, pollIntervalMs);
      };

      const pollFn = () => {
        if (cancelled) return;
        this.getAnalysisStatus(projectId, questionId).subscribe({
          next: (status) => {
            consecutiveErrors = 0;
            if (status.status === 'completed') {
              subscriber.next(status);
              subscriber.complete();
            } else if (status.status === 'failed') {
              subscriber.error(new Error(status.message || 'Analysis failed'));
            } else {
              // Still idle/in_progress - keep polling.
              scheduleNext();
            }
          },
          error: (error: HttpErrorResponse) => {
            const isTransient = transientStatuses.has(error?.status);
            consecutiveErrors++;
            if (!isTransient || consecutiveErrors >= maxConsecutiveErrors) {
              subscriber.error(error);
            } else {
              // Transient gateway/network failure - retry on the next interval
              // rather than aborting a job that is almost certainly still running.
              scheduleNext();
            }
          }
        });
      };

      // Start polling immediately
      pollFn();

      // Teardown: stop the loop if the caller unsubscribes (e.g. navigates away).
      return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
      };
    });
  }

  /**
   * Get analysis by project ID (if multiple questions need to be fetched)
   */
  getProjectAnalysis(projectId: string): Observable<ChatbotSummary[]> {
    return this.http.get<ChatbotSummary[]>(`${this.baseUrl}/project/${projectId}/analysis`);
  }

  /**
   * Get paginated quotes for a specific theme with optional filtering
   * @param questionId The question identifier
   * @param projectId The project identifier
   * @param themeId The theme identifier (numeric)
   * @param page Page number (0-based)
   * @param pageSize Items per page
   * @param sentimentFilters Optional sentiment filters
   * @returns Observable with paginated quotes response
   */
  getQuotesForThemePaginated(
    questionId: string,
    projectId: string,
    themeId: number,
    page: number = 0,
    pageSize: number = 50,
    sentimentFilters?: Sentiment[],
    version?: number
  ): Observable<{
    quotes: Quote[];
    hasMore: boolean;
    total: number;
    filteredTotal: number;
    page: number;
    pageSize: number;
  }> {
    let params = `?projectId=${projectId}&page=${page}&pageSize=${pageSize}`;

    if (sentimentFilters && sentimentFilters.length > 0) {
      params += `&sentiments=${sentimentFilters.join(',')}`;
    }

    if (version != null) {
      params += `&version=${version}`;
    }

    return this.http.get<{
      quotes: Quote[];
      hasMore: boolean;
      total: number;
      filteredTotal: number;
      page: number;
      pageSize: number;
    }>(`${this.baseUrl}/questions/${questionId}/themes/${themeId}/quotes${params}`);
  }

  /**
   * Get analysis balance for a specific question
   * @param questionId The question identifier
   * @param projectId The project identifier
   * @returns Observable with remaining balance response
   */
  getQuestionAnalysisBalance(questionId: string, projectId: string): Observable<RemainingBalanceResponse> {
    return this.http.get<RemainingBalanceResponse>(`${this.baseUrl}/questions/${questionId}/analysis-balance?projectId=${projectId}`);
  }

  /**
   * Update analysis balance for a specific question
   * @param questionId The question identifier
   * @param projectId The project identifier
   * @param balance The new balance value
   * @returns Observable with update balance response
   */
  updateQuestionAnalysisBalance(questionId: string, projectId: string, balance: number): Observable<UpdateBalanceResponse> {
    return this.http.put<UpdateBalanceResponse>(`${this.baseUrl}/questions/${questionId}/analysis-balance?projectId=${projectId}`, { Balance: balance });
  }


}